/**
 * /login — full GammaVision-styled login experience.
 *
 * Layout (top to bottom):
 *   AmbientStrip            (live SPX telemetry chips)
 *   GammaVision logo + wordmark
 *   Glass card: header + form + badges + ProofLine
 *   PricingStrip CTA card
 *
 * Background: ChartGridBackground canvas (financial chart-grid feel —
 * Bloomberg/TradingView-adjacent, not AI-demo-adjacent) + faint grid
 * overlay.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Logo from '../../components/chrome/Logo';
import ChartGridBackground from '../../components/login/ChartGridBackground';
import AmbientStrip from '../../components/login/AmbientStrip';
import ProofLine, { Proof } from '../../components/login/ProofLine';
import PricingStrip from '../../components/login/PricingStrip';
import { apiPost } from '../../lib/api';
import { useAuth } from '../../hooks/useAuth';
import { API_BASE_URL } from '../../lib/constants';
import type { AuthResponse } from '../../lib/types';

const PROOF: Proof[] = [
  {
    text: 'March 18: spot pinned at $580 — exactly the SPY King strike for that expiry.',
    cta: 'The level held. The model called it.',
  },
  {
    text: 'NDX flipped negative gamma at 19,840. Three days later realized vol doubled.',
    cta: 'Were you positioned?',
  },
  {
    text: 'QQQ Air Pocket between 480 and 487. It tore through both in 14 minutes.',
    cta: 'The map showed it first.',
  },
];

type ErrorKind = null | 'invalid' | 'suspended' | 'rate-limited' | 'network';

function errorMessage(kind: ErrorKind): React.ReactNode {
  switch (kind) {
    case 'invalid':      return 'Authentication failed. Check your username and password.';
    case 'suspended':    return (
      <>
        Account suspended due to failed payment.{' '}
        <a href="mailto:support@gammavision.app" style={{ color: 'var(--accent)', textDecoration: 'underline' }}>Contact support</a>.
      </>
    );
    case 'rate-limited': return 'Too many attempts. Wait a minute and try again.';
    case 'network':      return 'Connection lost. Check your internet and try again.';
    default:             return null;
  }
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { authed, loginWithJwt } = useAuth();
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Field is called `username` for legacy DOM compatibility (browser
  // password managers + autofill recognize this name), but it
  // accepts an email under the new JWT-auth flow.
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<ErrorKind>(null);

  // If already authed, bounce straight to /app/options.
  useEffect(() => {
    if (authed) {
      const target = (location.state as { from?: string } | null)?.from || '/app/options';
      navigate(target, { replace: true });
    }
  }, [authed, location.state, navigate]);

  useEffect(() => { usernameRef.current?.focus(); }, []);

  const clear = (cb: (v: string) => void) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    cb(e.target.value);
  };

  const shakeCard = () => {
    const card = cardRef.current;
    if (!card) return;
    card.classList.remove('shake');
    void card.offsetWidth; // reflow so the animation re-fires
    card.classList.add('shake');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!username || !password || busy) return;
    setBusy(true);
    setError(null);

    // New JWT-auth flow: POST /api/auth/login with {email, password}.
    // The endpoint accepts the legacy single-admin's email
    // (maneesh.gogineni@gmail.com — bootstrapped on first boot) plus
    // any user who's signed up via /api/auth/signup.
    try {
      const resp = await apiPost<AuthResponse>('/auth/login', {
        email: username.trim().toLowerCase(),
        password,
      });
      loginWithJwt(resp.token);
      setSuccess(true);
      cardRef.current?.classList.add('login-card--success');
      setTimeout(() => {
        const target = (location.state as { from?: string } | null)?.from || '/app/options';
        navigate(target, { replace: true });
      }, 700);
      return;
    } catch (err) {
      const ae = err as { status?: number; message?: string };
      const status = ae?.status ?? 0;

      if (status === 429) {
        setError('rate-limited'); shakeCard(); setBusy(false); return;
      }
      if (status === 401) {
        // Differentiate suspended-vs-invalid via the public probe.
        // /api/check-suspended is a stub today and always returns
        // false, but the wiring stays in place for when account
        // suspension on Stripe payment failure ships in Day 8+.
        try {
          const r2 = await fetch(`${API_BASE_URL}/check-suspended`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ username }),
          });
          if (r2.ok) {
            const body = await r2.json();
            setError(body.suspended ? 'suspended' : 'invalid');
          } else {
            setError('invalid');
          }
        } catch {
          setError('invalid');
        }
        shakeCard(); setBusy(false); return;
      }
      if (status === 0) {
        // ApiError with status=0 means network failure (fetch threw).
        setError('network'); shakeCard(); setBusy(false); return;
      }
      // 4xx / 5xx — generic invalid.
      setError('invalid'); shakeCard(); setBusy(false);
    }
  };

  const errMsg = errorMessage(error);

  return (
    <div className="login-page">
      <div className="login-bg-grid" aria-hidden="true" />
      <ChartGridBackground />
      <AmbientStrip />

      <div className="login-main">
        <div className="login-logo">
          <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <Logo width={56} height={28} title="GammaVision" />
            <h1 className="login-title">GAMMAVISION</h1>
            <div className="login-sub">GEX Terminal</div>
          </div>
        </div>

        <div className="login-card-wrap">
          <div ref={cardRef} className={`login-card${error ? ' login-card--err' : ''}`}>
            <div style={{ textAlign: 'center' }}>
              <span className="login-pill">
                <span className="dot" aria-hidden="true" /> QUANTITATIVE OPTIONS · TERMINAL
              </span>
            </div>
            <div className="login-head">
              <h2>Authorized Access Only</h2>
              <p>Enter credentials to enter the terminal.</p>
            </div>

            <form onSubmit={handleSubmit} aria-busy={busy} aria-label="Sign in">
              <div className="login-field">
                <label htmlFor="login-username">EMAIL</label>
                <input
                  id="login-username"
                  ref={usernameRef}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="off"
                  spellCheck={false}
                  required
                  value={username}
                  onChange={clear(setUsername)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); passwordRef.current?.focus(); } }}
                  className={error ? 'has-error' : undefined}
                  aria-invalid={!!error}
                />
              </div>
              <div className="login-field">
                <label htmlFor="login-password">PASSWORD</label>
                <input
                  id="login-password"
                  ref={passwordRef}
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={clear(setPassword)}
                  className={error ? 'has-error' : undefined}
                  aria-invalid={!!error}
                />
              </div>

              {errMsg && (
                <div role="alert" className="login-error">{errMsg}</div>
              )}

              <button
                type="submit"
                className={`login-submit${success ? ' login-submit--success' : ''}`}
                disabled={busy || !username || !password || success}
                aria-busy={busy}
              >
                {success ? (
                  <>AUTHENTICATED <span aria-hidden="true">✓</span></>
                ) : busy ? (
                  <>
                    <span className="login-spinner" aria-hidden="true" />
                    AUTHENTICATING…
                  </>
                ) : (
                  <>ACCESS TERMINAL <span aria-hidden="true">→</span></>
                )}
              </button>
            </form>

            <div className="login-badges">
              <span role="img" aria-label="Secure encryption">🔒 Encrypted</span>
              <span role="img" aria-label="Real-time data">⚡ Real-time</span>
              <span role="img" aria-label="Restricted access">🛡 Restricted</span>
            </div>

            <p style={{
              marginTop: '0.75rem', textAlign: 'center',
              fontSize: '0.78rem', color: 'var(--text-3)',
            }}>
              New here?{' '}
              <Link to="/signup" style={{ color: 'var(--accent)', textDecoration: 'underline', fontWeight: 600 }}>
                Create a free account →
              </Link>
            </p>

            <ProofLine items={PROOF} />
          </div>
        </div>

        <PricingStrip />

        <p style={{
          marginTop: '1.5rem', textAlign: 'center', fontSize: '0.7rem',
          color: 'var(--text-4)', letterSpacing: '0.04em',
        }}>
          By signing in you agree to our <Link to="/terms" style={{ color: 'inherit', textDecoration: 'underline' }}>terms of use</Link>. GammaVision is an analytical tool, not investment advice.
        </p>
      </div>
    </div>
  );
};

export default Login;
